import type { Customer } from '../../types/customer';
import type { LtlAddress, LtlBookingConfirmation, LtlHandlingUnit, LtlQuote, LtlShipment } from '../../types/ltl';
import type { State } from '../../types/reference-data';
import type { ShipFromAddress } from '../../types/ship-from';
import type { WeightUnit } from '../../types/weight';
import type { ShipFromOption } from '../freight-browser/freight-browser-types';
import type { RefObject } from 'react';

/**
 * The four wizard steps. There is intentionally NO 'quotes' step — in the new
 * design rates are always visible on the right-hand panel.
 */
export type WizardStepId = 'ship-from' | 'ship-to' | 'handling-units' | 'additional-details';

/**
 * How the wizard is opened:
 * - `new-from-quote`: fresh shipment from quote browser, mostly empty form but populate what we can from the quote browser.
 * - `new-direct`: new freight shipment from a direct launch of the booking wizard, no previous data to populate
 * - `load-saved-quote`: hydrated from a previously saved quote.
 * - `view-booked`: read-only confirmation view of an already-booked shipment.
 */
export const WIZARD_MODE = {
  NEW: 'new-from-quote',
  NEW_DIRECT: 'new-direct',
  LOAD_SAVED_QUOTE: 'load-saved-quote',
  VIEW_BOOKED: 'view-booked',
} as const;
export type WizardMode = (typeof WIZARD_MODE)[keyof typeof WIZARD_MODE];

/**
 * Narrow, wizard-shaped pickup services covering the nine Figma checkboxes. The
 * container maps these to the wide backend `LtlAdditionalServices` /
 * `FreightRequestAccessorials` enum at payload time — that mapping is NOT this
 * component's concern.
 *
 */
export type WizardPickupServices = {
  isInsidePickup: boolean;
  isConstructionSitePickup: boolean;
  isLiftgatePickup: boolean;
  isNotifyBeforePickup: boolean;
  isResidentialPickup: boolean;
  isDropAtTerminal: boolean;
  isGroceryConsolidationPickup: boolean;
  isLimitedAccess: boolean;
  isTradeshowPickup: boolean;
};

/**
 * Narrow, wizard-shaped delivery services covering the delivery-services checkboxes.
 * Mapped to the backend enum by the container.
 *
 * TODO(backend mapping): "Hold Shipment at Terminal" (`isHoldAtTerminal`) and
 * "Grocery Consolidation Delivery" (`isGroceryConsolidationDelivery`) have no
 * obvious `LtlAdditionalServices` field — confirm backend field names (plan open
 * question #5).
 */
export type WizardDeliveryServices = {
  isInsideDelivery: boolean;
  isConstructionSiteDelivery: boolean;
  isLiftgateDelivery: boolean;
  isNotifyBeforeDelivery: boolean;
  isResidentialDelivery: boolean;
  isHoldAtTerminal: boolean;
  isGroceryConsolidationDelivery: boolean;
  isAppointmentDelivery: boolean;
  isLimitedAccess: boolean;
  isTradeshowDelivery: boolean;
};

export type WizardTradeshow = {
  name: string;
  boothNumber: string;
};

/**
 * Wizard insurance shape. The freight-browser already defines an equivalent
 * `FreightBrowserInsurance`, but that type lives in browser-specific territory;
 * to keep the wizard self-contained (and avoid coupling the two surfaces) we
 * define a dedicated wizard insurance type here. The container maps it to the
 * backend `LtlInsurance` at payload time.
 */
export type WizardInsurance = {
  isInsured: boolean;
  conditionOfGoods: string;
  category: string;
  insuredValue: number;
  // Structurally compatible with the freight-browser's `FreightBrowserInsurance`
  // so the wizard can pass `insurance` straight to the shared `InsuranceSection`.
  marksAndNumbers: string;
};

export type WizardHandlingCharge = {
  isEnabled: boolean;
  value: number;
};

export type WizardReferenceItem = {
  id: string;
  type: string;
  value: string;
};

export type WizardReferences = {
  isEnabled: boolean;
  items: WizardReferenceItem[];
};

export type WizardShipmentServices = {
  isSortAndSegregate: boolean;
  isProtectFromFreeze: boolean;
  isProtectionFromHeat: boolean;
};

/**
 * The single form-state model for the booking wizard, organized by step. The
 * container owns hydration/persistence; the wizard reads/writes via
 * `onFormChange`.
 */
export type FreightBookingFormState = {
  // Ship From
  selectedShipFromId: string;
  originAddress: LtlAddress;
  shipmentDate: string;
  pickupServices: WizardPickupServices;
  tradeshowPickup: WizardTradeshow;

  // Ship To
  destinationAddress: LtlAddress;
  shouldSaveAsNewCustomer: boolean;
  deliveryServices: WizardDeliveryServices;
  tradeshow: WizardTradeshow;

  // Handling Units
  handlingUnits: LtlHandlingUnit[];

  // Additional Details
  shipmentServices: WizardShipmentServices;
  pickupReadyTime: string;
  pickupCloseTime: string;
  willArrangePickupSeparately: boolean;
  insurance: WizardInsurance;
  handlingCharge: WizardHandlingCharge;
  references: WizardReferences;
  pickupInstructions: string;
  deliveryInstructions: string;
  handlingInstructions: string;
  sellerFreightProviderID: number;
};

export type WizardStepProps = {
  stepNumber: number;
  title: string;
  isActive: boolean;
  isCompleted: boolean;
  isWarning?: boolean;
  onEdit: () => void;
  onCollapse?: () => void;
  summary?: import('react').ReactNode;
  children: import('react').ReactNode;
};

export type FreightBookingWizardProps = {
  isOpen: boolean;
  mode: WizardMode;
  onClose: () => void;

  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;

  shipFromOptions: ShipFromOption[];
  states: State[];
  // The weight unit commodity totals are displayed in (the seller's preferred
  // major unit — 'Pounds' or 'Kilograms'). Commodity weights may be stored in a
  // different unit and are converted to this for the handling-units summary.
  displayWeightUnit: WeightUnit;
  onCustomerSelect?: (customer: Customer) => void;
  onAddShipFrom?: () => void;

  // ss-ui-client drawer-placement escape hatch: portal the drawer into the app's
  // ModalManager stacking context so modals launched from within (Add Ship From)
  // can stack above it. When absent the drawer falls back to the default body portal.
  // See FreightDetails for the same pattern.
  portalRef?: RefObject<HTMLElement> | HTMLElement | null;
  zIndex?: number;

  // Optional commodity Product Name search wiring (handling-units step).
  productSuggestions?: string[];
  onProductSearch?: (query: string) => void;
  onProductSelect?: (commodityId: string, productName: string) => void;

  rates: LtlQuote[];
  isLoadingRates: boolean;
  rateError?: string;
  rateErrorCode?: string | null;
  connectedFreightProviderId?: number | null;
  selectedRate: LtlQuote | null;
  onSelectRate: (quote: LtlQuote) => void;
  onRefresh: () => void;
  shipFromAddress: ShipFromAddress;
  destinationAddress: LtlAddress;

  onSaveDraft: () => void;
  onBook: () => void;
  isSavingDraft: boolean;
  isBooking: boolean;
  onDeleteDraft?: () => void;
  isDeletingDraft?: boolean;
  canDeleteDraft?: boolean;

  // Pre-completion (used by load-saved-quote hydration): which steps render as
  // already completed/collapsed and which step is initially active. Both are read
  // once on mount (lazy initial state); they do not drive the wizard afterward.
  initialCompletedSteps?: Partial<Record<WizardStepId, boolean>>;
  initialActiveSteps?: readonly WizardStepId[];

  bookingConfirmation?: LtlBookingConfirmation;
  bookedShipment?: LtlShipment;
};
